import { EventWithDetail, OrgFullDetail, UpdateEvent } from "@garther/interfaces";
import { Box, Button, ImageInput, Input, TiptapEditor } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";

import { useUpdateEvent } from "@/features/event/hooks/use-events-query";
import { useUploadImage } from "@/features/public/hooks/use-public-query";

interface EditEventPageProps {
  org: OrgFullDetail;
  event: EventWithDetail;
}

export function EditEventPage({ org, event }: EditEventPageProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { mutateAsync: uploadImage } = useUploadImage();

  const { mutate: updateEvent, isPending: isSubmitting } = useUpdateEvent({
    onSuccess: () => {
      toast.success(t("common.saveSuccess"));
      navigate({
        to: "/org/$orgId/event/$eventId",
        params: { orgId: org.id, eventId: event.id }
      });
    }
  });

  const form = useForm<UpdateEvent>({
    defaultValues: {
      title: event.title,
      description: event.description,
      banner: event.banner || "",
      isPublic: event.isPublic
    },
    onSubmit: ({ value }) => {
      updateEvent({
        eventId: event.id,
        data: {
          title: value.title?.trim(),
          description: value.description?.trim(),
          banner: value.banner?.trim() || undefined,
          isPublic: value.isPublic
        }
      });
    }
  });

  const onCancel = () => {
    navigate({
      to: "/org/$orgId/event/$eventId",
      params: { orgId: org.id, eventId: event.id }
    });
  };

  return (
    <div className="py-4 md:py-6 lg:py-8 px-0 sm:px-2 md:px-4 lg:px-8 space-y-6">
      <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto">
        <div className="px-4 md:px-6">
          <p className="text-responsive-2xl font-light text-text-primary mb-1 font-literata uppercase">
            {t("event.edit.title")}
          </p>
          <p className="text-responsive-base font-normal text-text-secondary">{event.title}</p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <Box className="space-y-6 mb-6" label={t("org.edit.basicInfo")}>
            {/* Banner */}
            <form.Field name="banner">
              {(field) => (
                <ImageInput
                  value={field.state.value || ""}
                  onChange={field.handleChange}
                  uploadFn={uploadImage}
                  label={t("event.edit.banner")}
                  previewSize="w-full h-48 md:h-64"
                  previewClassName="w-full h-48 md:h-64 object-cover"
                  disabled={isSubmitting}
                  toastSuccess={toast.success}
                  toastError={toast.error}
                  successMessage={t("org.edit.coverUploadSuccess")}
                  errorMessage={t("org.edit.coverUploadError")}
                  invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                  fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                  alt="Banner preview"
                />
              )}
            </form.Field>

            {/* Title */}
            <form.Field
              name="title"
              validators={{
                onChange: ({ value }) => (!value?.trim() ? t("event.edit.errors.titleRequired") : undefined)
              }}
            >
              {(field) => (
                <Input
                  label={t("event.edit.eventTitle")}
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value)}
                  required
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Description */}
            <form.Field
              name="description"
              validators={{
                onChange: ({ value }) =>
                  !value?.trim() ? t("event.edit.errors.descriptionRequired") : undefined
              }}
            >
              {(field) => (
                <TiptapEditor
                  label={t("event.edit.description")}
                  required
                  error={field.state.meta.errors[0] || undefined}
                  value={field.state.value || ""}
                  onChange={field.handleChange}
                  disabled={isSubmitting}
                  placeholder={t("event.edit.descriptionPlaceholder")}
                />
              )}
            </form.Field>

            {/* Visibility */}
            <form.Field name="isPublic">
              {(field) => (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={field.state.value}
                      onClick={() => field.handleChange(!field.state.value)}
                      disabled={isSubmitting}
                      className={`relative w-10 h-6 rounded-full transition-colors ${field.state.value ? "bg-background-dark" : "bg-gray-300"}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${field.state.value ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </button>
                    <span className="font-medium text-text-primary">{t("event.edit.isPublic")}</span>
                  </div>
                  <p className="text-sm text-text-secondary">{t("event.edit.isPublicDescription")}</p>
                </div>
              )}
            </form.Field>
          </Box>

          <div className="flex gap-4 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              padding="px-12 py-3"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <form.Subscribe selector={(state) => state.isDirty}>
              {(isDirty) => (
                <Button
                  type="submit"
                  padding="px-12 py-3"
                  variant="default"
                  disabled={isSubmitting || !isDirty}
                >
                  {isSubmitting ? t("common.saving") : t("common.save")}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </div>
    </div>
  );
}
